import { Fragment, Suspense } from 'react'
import TransText from 'next-translate/TransText'
import { Paragraph } from '../stateless/typo/paragraph'
import { Header2, Header3 } from '../general/header'
import dynamic from 'next/dynamic'
import { Link } from '../stateless/typo/link'
import { routeParseAbsolute } from '@app/lib/router-handler'

export type SectionProps = {
  text?: string
  as?: 'p' | 'list' | 'h2' | 'h3' | 'image'
  list?: string[]
  extraProps?: any
  details?: string // details when using headers h1,h2,h3
}
export type RenderTextProps = {
  children: string
  paragraph?: boolean
  lkey?: string
  index: number
  i: number
  extraProps?: any
  lang?: string
}
export type SectionPropsAll = {
  item: SectionProps
  index: number
  lang?: string
}

// TODO: map all top objects
const dynamicMap = {
  JavascriptUsage: dynamic(
    () =>
      import('../stateless/marketing/js-usage').then(
        (mod) => mod.JavascriptUsage
      ),
    {
      ssr: true,
    }
  ),
  ThemedImage: dynamic(
    () =>
      import('../general/themes/theme-image').then((mod) => mod.ThemedImage),
    {
      ssr: false,
    }
  ),
}

const linkCss = 'text-blue-600 hover:text-blue-500'

const RenderText = ({
  children,
  lkey,
  index,
  i,
  paragraph,
  extraProps,
  lang,
}: RenderTextProps) => {
  const Base = paragraph ? 'p' : 'li'

  if (children.startsWith('<0>')) {
    return (
      <TransText
        key={lkey}
        text={children}
        components={{
          '0': <Base key={`list-item-${index}-${i}`} />,
          link: (
            <Link href={routeParseAbsolute('/', lang)} className={linkCss} />
          ),
          link1: <Link href={extraProps.link1} className={linkCss} />,
          link2: <Link href={extraProps.link2} className={linkCss} />,
        }}
      />
    )
  }

  return <Base key={lkey}>{children}</Base>
}

export const RenderSection = ({ item, index, lang }: SectionPropsAll) => {
  const { as, text, list, extraProps = {}, details } = item

  if (as?.startsWith('dynamic:')) {
    const part = as.split('dynamic:')[1] as keyof typeof dynamicMap

    const DynamicComponent = part in dynamicMap ? dynamicMap[part] : 'div'

    return (
      <Suspense fallback={<div>...</div>}>
        <DynamicComponent {...extraProps} />
      </Suspense>
    )
  }

  if (as && ['h2', 'h3'].includes(as)) {
    const Wrapper = details ? 'div' : Fragment
    const H = as === 'h2' ? Header2 : Header3

    return (
      <Wrapper>
        <H>{text}</H>
        {details ? (
          <RenderText
            key={`article-section-item-${index}`}
            index={index}
            extraProps={extraProps}
            i={0}
            paragraph
            lang={lang}
          >
            {details}
          </RenderText>
        ) : null}
      </Wrapper>
    )
  }

  if (as === 'p') {
    return <Paragraph>{text}</Paragraph>
  }

  if (as === 'image') {
    return (
      <figure className='text-center pt-2 flex place-items-center flex-col space-y-2'>
        <dynamicMap.ThemedImage
          src={extraProps.src}
          srcDark={extraProps.srcDark}
          alt={text + ''}
          width={extraProps.width ?? 686}
          height={extraProps.height ?? 506}
          className='shadow'
        />
        <figcaption>{extraProps?.caption ?? text}</figcaption>
      </figure>
    )
  }

  if (as === 'list') {
    return (
      <>
        {index === 0 ? <Header2>{text}</Header2> : <Header3>{text}</Header3>}
        {list && Array.isArray(list) ? (
          <>
            <ul className='list-disc px-8 space-y-4'>
              {list.map((item, i) => {
                const lkey = `article-section-item-${index}-${i}`

                return (
                  <RenderText
                    key={lkey}
                    index={index}
                    i={i}
                    extraProps={extraProps}
                    lang={lang}
                  >
                    {item}
                  </RenderText>
                )
              })}
            </ul>
          </>
        ) : null}
      </>
    )
  }
  return null
}
